/**
 * Draw an outline on a Google map by clicking around it (backlog #694,
 * GID-Session-020). Used by the New drop dialog ("Draw the area instead") and
 * the Drop page ("Add an area").
 *
 * A click adds a corner; the outline fills in as you go, with a rubber-band
 * line from the last corner to the cursor and back to the first. Click the
 * first corner, double-click, or press Enter to close it; Backspace undoes a
 * corner; Escape cancels. Everything already on the map is left alone, except
 * that the shapes passed in `ignore` stop taking clicks while drawing (a lot
 * under the cursor would otherwise swallow the corner — Bug #384's lesson).
 * `window.__gidAreaDraw` exposes the controller while a draw is active, so a
 * headless probe can add corners by coordinate instead of by pixel.
 */
const COLOUR = '#FACC15';
const CLOSE_PX = 12; // a click this close to the first corner closes the outline

export function startAreaDraw(maps, map, { onChange = () => {}, onFinish = () => {}, onCancel = () => {}, ignore = [], keys = true } = {}) {
  const points = []; // maps.LatLng, in the order clicked
  const dots = [];   // one marker per corner
  const listeners = [];
  let active = true;

  const outline = new maps.Polygon({ map, paths: [], strokeColor: COLOUR, strokeOpacity: 0.95, strokeWeight: 2.5, fillColor: COLOUR, fillOpacity: 0.08, clickable: false, zIndex: 50 });
  const band = new maps.Polyline({ map, path: [], strokeColor: COLOUR, strokeOpacity: 0.55, strokeWeight: 1.5, clickable: false, zIndex: 50 });
  const was = { draggableCursor: map.get('draggableCursor'), disableDoubleClickZoom: map.get('disableDoubleClickZoom') };
  map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
  const restore = ignore.filter((o) => o && typeof o.setOptions === 'function').map((o) => {
    const clickable = typeof o.get === 'function' ? o.get('clickable') : true;
    o.setOptions({ clickable: false });
    return () => o.setOptions({ clickable: clickable !== false });
  });

  const dotIcon = (first) => ({ path: maps.SymbolPath.CIRCLE, scale: first ? 7 : 4.5, fillColor: COLOUR, fillOpacity: 1, strokeColor: '#0b0f14', strokeWeight: 1.5 });
  // Screen distance between two points at the current zoom — "did that click land on the first corner?"
  function pxBetween(a, b) {
    const proj = map.getProjection();
    if (!proj) return Infinity;
    const s = 2 ** map.getZoom();
    const pa = proj.fromLatLngToPoint(a), pb = proj.fromLatLngToPoint(b);
    return Math.hypot((pa.x - pb.x) * s, (pa.y - pb.y) * s);
  }
  const ring = () => points.map((ll) => [ll.lat(), ll.lng()]);
  const redraw = () => { outline.setPath(points); band.setPath([]); onChange(points.length); };

  function add(latLng) {
    if (!active) return;
    points.push(latLng);
    dots.push(new maps.Marker({ map, position: latLng, clickable: false, zIndex: 60, icon: dotIcon(points.length === 1) }));
    redraw();
  }
  function undo() {
    if (!active || !points.length) return;
    points.pop();
    const dot = dots.pop();
    if (dot) dot.setMap(null);
    redraw();
  }
  function stop() {
    if (!active) return;
    active = false;
    listeners.forEach((l) => maps.event.removeListener(l));
    if (keys) document.removeEventListener('keydown', onKey, true);
    outline.setMap(null);
    band.setMap(null);
    dots.forEach((d) => d.setMap(null));
    restore.forEach((fn) => fn());
    map.setOptions({ draggableCursor: was.draggableCursor ?? null, disableDoubleClickZoom: was.disableDoubleClickZoom ?? false });
    if (window.__gidAreaDraw === api) delete window.__gidAreaDraw;
  }
  function finish() {
    if (!active || points.length < 3) return false;
    const r = ring();
    stop();
    onFinish(r);
    return true;
  }
  function cancel() {
    if (!active) return;
    stop();
    onCancel();
  }
  function onKey(e) {
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    else if (e.key === 'Enter') { e.preventDefault(); finish(); }
    else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); undo(); }
  }

  listeners.push(map.addListener('click', (e) => {
    if (!e.latLng) return;
    if (points.length >= 3 && pxBetween(e.latLng, points[0]) < CLOSE_PX) finish();
    else add(e.latLng);
  }));
  // A double-click arrives as two clicks on the same spot, then dblclick: drop the repeat and close.
  listeners.push(map.addListener('dblclick', () => {
    while (points.length >= 2 && pxBetween(points[points.length - 1], points[points.length - 2]) < 4) undo();
    finish();
  }));
  listeners.push(map.addListener('mousemove', (e) => {
    if (!active || !points.length || !e.latLng) return;
    band.setPath(points.length >= 2 ? [points[points.length - 1], e.latLng, points[0]] : [points[0], e.latLng]);
  }));
  if (keys) document.addEventListener('keydown', onKey, true);

  const api = {
    add: (lat, lng) => add(new maps.LatLng(Number(lat), Number(lng))),
    undo,
    finish,
    cancel,
    ring,
    count: () => points.length,
    active: () => active
  };
  window.__gidAreaDraw = api;
  return api;
}
